/**
 * Static methods which validate values passed to functions (or for other validation uses).  Each throws an Error if
 * the passed value(s) is not valid; exits silently if valid.
 * @private
 */
class BamArgValidator {

    /**
     * Validates the args passed to a function, throws an error if invalid values found
     * @param {object} passedArgs the "arguments" property of the calling function
     * @param {array} argNamesArr Array of function-param names (used in error-messaging)
     * @param {array} expectedTypesArr array of types (first el for first arg, second el for second arg, etc.).  More than one
     *   type is supported; separate supported types with a pipe.  For the purposes of this validation, the following
     *   are valid types: the standard types ("undefined", "object", "boolean", "number", "string", "symbol", and
     *   "function"), plus "null" and "array".  So for example, if a string or null is accepted for a given arg, the
     *   type would be "string|null"
     * @param {array} requiredBooleansArr An array of booleans; array must be same length as `expectedTypesArr`. Els should be
     *   true for required values, false otherwise; e.g., this array requires the first two of a possible four args:
     *   [true, true, false, false].  For the purposes of this method, an arg has been passed if it is not undefined.
     * @throws Error if invalid or missing args found
     */
    static validateArgs(passedArgs, argNamesArr, expectedTypesArr, requiredBooleansArr) {
        if (argNamesArr.length !== expectedTypesArr.length) {
            throw new Error('expectedTypesArr, and argNamesArr must be the same length');
        }

        if (passedArgs.length > argNamesArr.length) {
            throw new Error('this function takes ' + argNamesArr.length + ' args, but you passed ' + passedArgs.length + '.');
        }

        let required;
        let argVal;
        let allowedTypes;
        let validTypeFound;
        let argName;
        for (let i = 0; i < expectedTypesArr.length; ++i) {
            argName = argNamesArr[i];
            argVal = (i >= passedArgs.length ? undefined : passedArgs[i]);
            required = requiredBooleansArr[i];
            if (required && argVal === undefined) {
                throw new Error('The "' + argName + '" param is required.');
            }
            allowedTypes = expectedTypesArr[i].split('|');
            validTypeFound = false;
            for (let i = 0; i < allowedTypes.length; ++i) {
                const allowedType = allowedTypes[i];
                if (
                    (argVal === undefined && !required) ||
                    (allowedType === 'array' && Array.isArray(argVal)) ||
                    (allowedType === 'null' && argVal === null) ||
                    (typeof argVal === allowedType)) {
                    validTypeFound = true;
                    break;
                }
            }
            if (!validTypeFound) {
                throw new Error('The "' + argName + '" param is an unsupported type; supported type(s): ' +
                    expectedTypesArr[i].replace(/\|/g, ', '));
            }
        }
    }


    /**
     * Throws an error if person is not a valid statsapi person node
     * @param person
     * @throws Error if invalid person node
     */
    static validateStatsapiPerson(person) {
        if (typeof person !== 'object' || person.id === undefined || person.fullName === undefined) {
            throw new Error('person must be a valid statsapi person node, as returned by various Sports Data APIs.');
        }
    }

    /**
     * Throws an error if team is not a valid statsapi team node
     * @param team
     * @throws Error if invalid team node
     */
    static validateStatsapiTeam(team) {
        if (typeof team !== 'object' ||
            !BamArgValidator._hasAllProps(team, ['id', 'name', 'teamName', 'shortName'])) {
            throw new Error('team must be a valid statsapi team node, as returned by the Teams API.');
        }
    }

    /**
     * Throws an error if game is not a valid statsapi game node
     * @param game
     * @throws Error if invalid game node
     */
    static validateStatsapiGame(game) {
        if (typeof game !== 'object' ||
            !BamArgValidator._hasAllProps(game, ['gamePk', 'gameType', 'gameDate', 'status', 'teams'])) {
            throw new Error('game must be a valid statsapi game node, as returned by the Schedule API.');
        }
    }

    /**
     * Throws an error if game is not a valid statsapi event node
     * @param nonGameEvent
     * @throws Error if invalid event node
     */
    static validateStatsapiNonGameEvent(nonGameEvent) {
        if (typeof nonGameEvent !== 'object' ||
            !BamArgValidator._hasAllProps(nonGameEvent, ['id', 'eventType', 'eventDate', 'venue', 'teams'])) {
            throw new Error('non game event must be a valid statsapi event node, as returned by the Schedule API.');
        }
    }

    /**
     * Throws an error if s is not a value of type number, or a string which can be converted to a number
     * @param s
     * @throws Error if s isn't a number
     */
    static validateNumber(s) {
        if (typeof s === 'number') {
            return;
        }
        if (typeof s === 'string' && !isNaN('' + s)) {
            return;
        }
        throw new Error('not convertible to a numeric value: ' + s);
    }

    /**
     * Passed a value (typically of a function param), throws an error if that value is not in an array of accepted
     * values.
     * @param {*} val The value we're evaluating for validity
     * @param {array} allowedVals Allowed values
     * @throws Error if the value is not valid
     */
    static validateAllowed(val, allowedVals) {
        let allowed = (Array.isArray(allowedVals) ? allowedVals : [allowedVals]);
        if (allowed.indexOf(val) === -1) {
            throw new Error('' + val + ' is not valid; accepted values are: ' + allowed);
        }
    }

    /**
     * Returns false if obj does not have all the properties in the required-props array, true if valid
     * @param obj An object
     * @param requiredPropsArr And array of strings; each el is the name of a property which the object must have
     * @private
     */
    static _hasAllProps(obj, requiredPropsArr) {
        for (let i = 0; i < requiredPropsArr.length; ++i) {
            const prop = requiredPropsArr[i];
            if (obj[prop] === undefined) {
                return false;
            }
        }
        return true;
    }

}

export default BamArgValidator;
